import React, { useCallback, useState } from "react";
import SEO from "@/components/SEO";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import { Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { publicClient } from "@/lib/apiClient";
import PartnerCompanyContact from "@/components/partner/PartnerCompanyContact";
import PartnerServiceAreas from "@/components/partner/PartnerServiceAreas";
import PartnerTradesServices from "@/components/partner/PartnerTradesServices";
import PartnerPricingByTrade from "@/components/partner/PartnerPricingByTrade";
import PartnerCrewCapacity from "@/components/partner/PartnerCrewCapacity";
import PartnerAvailability from "@/components/partner/PartnerAvailability";
import PartnerUploads from "@/components/partner/PartnerUploads";
import PartnerPaymentSignature from "@/components/partner/PartnerPaymentSignature";
import { todayIsoDate } from "@/components/partner/partnerHelpers";

const BREADCRUMBS = [{ label: "Home", to: "/" }, { label: "Partner With Us" }];

function makeInitialData() {
  return {
    // Section 1
    company_name: "", dba_trade_name: "",
    contact_first_name: "", contact_middle_name: "", contact_last_name: "",
    role_title: "", phone: "", email: "", website_url: "",
    business_street1: "", business_street2: "",
    business_city: "", business_state: "", business_zip: "", business_country: "United States",
    company_id_vendor_number: "",
    // Section 2
    service_areas_covered: [], service_areas_other: "",
    max_travel_radius_miles: "", travel_limitations: "",
    // Section 3
    trades_offered: [], trades_other: "",
    // Section 4
    pricing_basis: "", trade_pricing: {}, pricing_notes: "",
    // Section 5
    crew_size: "", num_crews: "",
    project_types: [], capacity_limits: "", team_notes: "",
    // Section 6
    current_availability: "", preferred_start: "", scheduling_constraints: "",
    // Section 7
    w9_file: null, coi_file: null, wc_file: null, id_file: null,
    // Section 8
    payment_methods: [], payment_methods_other: "",
    signer_first_name: "", signer_middle_name: "", signer_last_name: "",
    typed_signature: "", signature_date: todayIsoDate(),
    esign_acknowledged: false,
    // honeypot
    website: "",
  };
}

function validate(data) {
  const required = [
    ["company_name", "Company Name"],
    ["contact_first_name", "Contact First Name"],
    ["contact_last_name", "Contact Last Name"],
    ["phone", "Phone"],
    ["email", "Email"],
    ["business_street1", "Business Street Address"],
    ["business_city", "Business City"],
    ["business_state", "Business State"],
    ["business_zip", "Business ZIP"],
    ["max_travel_radius_miles", "Maximum Travel Radius"],
    ["pricing_basis", "Pricing Basis"],
    ["crew_size", "Crew Size"],
    ["num_crews", "Number of Crews"],
    ["current_availability", "Current Availability"],
    ["preferred_start", "Preferred Start Timeframe"],
    ["signer_first_name", "Signer First Name"],
    ["signer_last_name", "Signer Last Name"],
    ["typed_signature", "Electronic Signature"],
    ["signature_date", "Signature Date"],
  ];
  for (const [k, label] of required) {
    if (!data[k] || (typeof data[k] === "string" && !data[k].trim())) {
      return `Missing required field: ${label}.`;
    }
  }
  if (!Array.isArray(data.service_areas_covered) || data.service_areas_covered.length === 0) {
    return "Please select at least one Service Area.";
  }
  if (!Array.isArray(data.trades_offered) || data.trades_offered.length === 0) {
    return "Please select at least one Trade.";
  }
  if (!Array.isArray(data.project_types) || data.project_types.length === 0) {
    return "Please select at least one Project Type.";
  }
  if (!Array.isArray(data.payment_methods) || data.payment_methods.length === 0) {
    return "Please select at least one Preferred Payment Method.";
  }
  if (!data.w9_file) return "Please upload your W-9 Form.";
  if (!data.coi_file) return "Please upload your Certificate of Insurance.";
  if (!data.id_file) return "Please upload your Government ID.";
  if (!data.esign_acknowledged) return "Please acknowledge the electronic signature consent.";

  // Signature must match signer name
  const expected = [data.signer_first_name, data.signer_middle_name, data.signer_last_name]
    .filter(Boolean).join(" ").trim().toLowerCase();
  if (data.typed_signature.trim().toLowerCase() !== expected) {
    return "Your electronic signature must match the signer's full legal name exactly.";
  }
  return null;
}

export default function Partner() {
  const [data, setData] = useState(makeInitialData);
  const [state, setState] = useState({ loading: false, success: false, error: "" });

  // Curried setter for string-input handlers: set("field_name") -> (event)=>{...}
  const set = useCallback(
    (k) => (e) => setData((p) => ({ ...p, [k]: e.target.value })),
    [],
  );
  // Direct setter for non-event cases (checkboxes, files, arrays)
  const setRaw = useCallback((k, v) => setData((p) => ({ ...p, [k]: v })), []);
  const setFile = useCallback((k, payload) => setData((p) => ({ ...p, [k]: payload })), []);

  const onError = useCallback((msg) => setState((s) => ({ ...s, error: msg })), []);

  const submit = async (e) => {
    e.preventDefault();
    const err = validate(data);
    if (err) {
      setState({ loading: false, success: false, error: err });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    setState({ loading: true, success: false, error: "" });
    try {
      await publicClient.post("/subcontractors", data);
      setState({ loading: false, success: true, error: "" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setState({
        loading: false,
        success: false,
        error: err?.response?.data?.detail || "Could not submit, please try again.",
      });
    }
  };

  const handleReset = () => {
    setData(makeInitialData());
    setState({ loading: false, success: false, error: "" });
  };

  if (state.success) {
    return (
      <>
        <SEO title="Application Received | Black Ridge Construction" noIndex />
        <section className="bg-br-black text-white">
          <div className="container-br py-12 lg:py-16">
            <BreadcrumbNav dark items={BREADCRUMBS} />
          </div>
        </section>
        <section className="section-pad bg-br-paper">
          <div className="container-br max-w-2xl">
            <div className="bg-white border border-gray-200 rounded p-8" data-testid="partner-form-success">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="text-emerald-600 shrink-0" size={28} />
                <div>
                  <h1 className="font-display text-2xl tracking-widest uppercase mb-2">Application Received</h1>
                  <p className="text-sm leading-relaxed text-gray-700">
                    Thank you for applying to partner with Black Ridge Construction. We received your subcontractor intake form and supporting documents. Our team will review your application and reach out about specific project opportunities.
                  </p>
                  <button onClick={handleReset} className="mt-5 text-xs uppercase tracking-widest font-bold text-br-bronze hover:underline" data-testid="partner-submit-another">
                    Submit another application →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <SEO
        title="Subcontractor & Trade Partner Opportunities | Black Ridge Construction"
        description="Apply to join the Black Ridge Construction subcontractor network. We work with insured, communicative trade partners across Austin and Central Texas."
      />

      <section className="bg-br-black text-white">
        <div className="container-br py-12 lg:py-16">
          <BreadcrumbNav dark items={BREADCRUMBS} />
          <div className="max-w-3xl mt-5">
            <div className="br-eyebrow text-br-bronze-2">Subcontractor / Vendor Intake</div>
            <h1 className="br-headline text-4xl sm:text-5xl mt-3">Subcontractor & Trade Partner Application</h1>
            <p className="mt-4 text-gray-300">
              Please complete this intake form so we can match you with the right Black Ridge Construction projects. Provide company details, trade rates, compliance documents, and your electronic signature. Plan on 10–15 minutes — please have your W-9, COI, and ID ready as PDFs or photos.
            </p>
          </div>
        </div>
      </section>

      <section className="section-pad bg-br-paper">
        <div className="container-br grid lg:grid-cols-12 gap-8">
          <form onSubmit={submit} className="lg:col-span-9 space-y-5" data-testid="partner-form" noValidate>
            {state.error && (
              <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded sticky top-4 z-10 shadow-md" data-testid="partner-form-error">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>{state.error}</span>
              </div>
            )}

            <PartnerCompanyContact data={data} set={set} />
            <PartnerServiceAreas data={data} setRaw={setRaw} />
            <PartnerTradesServices data={data} setRaw={setRaw} />
            <PartnerPricingByTrade data={data} setRaw={setRaw} />
            <PartnerCrewCapacity data={data} set={set} setRaw={setRaw} />
            <PartnerAvailability data={data} set={set} />
            <PartnerUploads data={data} setFile={setFile} onError={onError} />
            <PartnerPaymentSignature data={data} set={set} setRaw={setRaw} />

            {/* honeypot */}
            <input type="text" value={data.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

            <button
              type="submit"
              disabled={state.loading}
              className="br-btn-primary w-full justify-center text-base"
              data-testid="partner-submit"
            >
              {state.loading ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              {state.loading ? "Submitting..." : "Submit Subcontractor Application"}
            </button>
            <p className="text-[11px] text-gray-500 text-center">
              By submitting, you agree Black Ridge Construction may contact you about partnership opportunities and verify provided documents.
            </p>
          </form>

          <aside className="lg:col-span-3 space-y-4">
            <div className="p-5 bg-white border border-gray-200 rounded lg:sticky lg:top-24">
              <h3 className="font-display tracking-widest uppercase text-sm mb-3">Application Checklist</h3>
              <ul className="text-xs text-gray-700 space-y-1.5">
                <li>• Company &amp; contact details</li>
                <li>• Service areas &amp; trades</li>
                <li>• Trade-specific pricing</li>
                <li>• Crew size + project capacity</li>
                <li>• Availability + start date</li>
                <li>• W-9 (PDF, ≤3MB)</li>
                <li>• Certificate of Insurance (PDF, ≤3MB)</li>
                <li>• Workers Comp (if employees)</li>
                <li>• Government ID (image, ≤3MB)</li>
                <li>• Payment method preference</li>
                <li>• Electronic signature</li>
              </ul>
            </div>
            <div className="p-5 bg-br-black text-white rounded">
              <h3 className="font-display tracking-widest uppercase text-sm mb-2 text-br-bronze-2">Who We Look For</h3>
              <ul className="text-xs text-gray-300 space-y-1.5">
                <li>• Insured &amp; licensed where applicable</li>
                <li>• Reliable scheduling &amp; clear comms</li>
                <li>• Quality work, clean job sites</li>
                <li>• Long-term relationship mindset</li>
              </ul>
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
